// @flow

import get from 'lodash/get';
import { errors as ERRORS_ENUM } from 'Enum';
import {
  sentry as SENTRY_CONFIG,
  debug as DEBUG_CONFIG,
  environment as ENVIRONMENT,
  version as APP_VERSION,
  store as STORE_CONFIG,
} from 'Config';
import { RiseArtSentryError } from 'shared_services/riseart/errors/SentryError';

const {
  sentry: IS_SENTRY_ENABLED,
  console: SHOW_CONSOLE_LOGS,
  logStoreState: LOG_STORE_STATE,
} = DEBUG_CONFIG;
const { levels: ERROR_LEVELS } = ERRORS_ENUM;

export const MESSAGE_TYPES = {
  ...ERROR_LEVELS,
};

export const ERROR_HANDLERS = {
  ...ERRORS_ENUM.handlers,
};

/**
 * LogMessageType
 */
type LogMessageType = {
  message: string,
  level: string,
  data?: Object,
};

/**
 * LogHttpState
 */
type LogHttpState = { ip?: string, ips?: Array<string>, url?: string, userAgent?: string };

/**
 * getReduxStateData
 *
 * @param {Object} reduxStore
 * @returns {Object} redux store state if allowed in config and token, visitor_id and user_id
 */
function getReduxStateData(reduxStore: Object) {
  const reduxStoreState = reduxStore && reduxStore.getState();

  if (!reduxStoreState) {
    return {};
  }

  const authStoreState = get(reduxStoreState, STORE_CONFIG.keys.auth);
  const meStoreState = get(reduxStoreState, STORE_CONFIG.keys.me);

  return {
    ...(LOG_STORE_STATE && reduxStoreState ? { state: reduxStoreState } : {}),
    token: get(authStoreState, 'data.token', null),
    visitor_id: get(meStoreState, 'data.visitor.id', null),
    user_id: get(meStoreState, 'data.user.id', null),
  };
}

/**
 * getHttpStateData
 *
 * @param {Object} httpRequest
 * @returns {LogHttpState}
 */
function getHttpStateData(httpRequest: Object): LogHttpState {
  const data: LogHttpState = {};

  if (!httpRequest) {
    return data;
  }

  if (httpRequest.ip) {
    data.ip = httpRequest.ip;
  }

  if (httpRequest.ips) {
    data.ips = httpRequest.ips;
  }

  if (httpRequest.originalUrl) {
    data.url = httpRequest.originalUrl;
  }

  if (httpRequest.headers && httpRequest.headers['user-agent']) {
    data.userAgent = httpRequest.headers['user-agent'];
  }

  return data;
}

/**
 * RiseartLogger
 */
export const RiseartLogger: Object = {
  sentrySdk: {},
  reduxStore: null,
  httpRequest: null,
  configured: false,
  initialized: false,

  /**
   * Config
   *
   * @param {Object} sdk
   * @returns {Object}
   */
  config: (sdk: Object): Object => {
    if (RiseartLogger.isConfigured) {
      return RiseartLogger;
    }

    if (RiseartLogger.isSentryEnabled) {
      const isProfilingEnabled =
        process.env.REACT_APP_PROFILING === 'true' && DEBUG_CONFIG.profiling;
      const sentryProfileRate = isProfilingEnabled
        ? parseFloat(process.env.REACT_APP_SENTRY_PROFILE_RATE)
        : 0;
      RiseartLogger.sentrySdk = sdk;
      RiseartLogger.sentrySdk.init({
        dsn: SENTRY_CONFIG.accountEndpoint,
        environment: ENVIRONMENT,
        release: APP_VERSION.number,
        ...(isProfilingEnabled &&
        (RiseartLogger.sentrySdk.browserTracingIntegration ||
          RiseartLogger.sentrySdk.browserProfilingIntegration ||
          RiseartLogger.sentrySdk.nodeProfilingIntegration)
          ? {
              integrations: [
                ...(RiseartLogger.sentrySdk.browserTracingIntegration
                  ? [RiseartLogger.sentrySdk.browserTracingIntegration()]
                  : []),
                ...(RiseartLogger.sentrySdk.browserProfilingIntegration
                  ? [RiseartLogger.sentrySdk.browserProfilingIntegration()]
                  : []),
                ...(RiseartLogger.sentrySdk.nodeProfilingIntegration
                  ? [RiseartLogger.sentrySdk.nodeProfilingIntegration()]
                  : []),
              ],
            }
          : null),
        ...(isProfilingEnabled
          ? {
              tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACE_RATE),
              profilesSampleRate: sentryProfileRate,
              profileSessionSampleRate: sentryProfileRate,
              profileLifecycle: 'trace',
            }
          : null),
      });
    }

    RiseartLogger.configured = true;

    return RiseartLogger;
  },

  /**
   * isSentryEnabled
   *
   * @returns {boolean}
   */
  get isSentryEnabled(): boolean {
    return IS_SENTRY_ENABLED;
  },

  /**
   * isConfigured
   *
   * @returns {boolean}
   */
  get isConfigured(): boolean {
    return RiseartLogger.configured;
  },

  /**
   * isInitialized
   *
   * @returns {boolean}
   */
  get isInitialized(): boolean {
    return RiseartLogger.isConfigured && RiseartLogger.initialized;
  },

  /**
   * init
   *
   * @param {Object} reduxStore
   * @returns {void}
   */
  init: ({
    reduxStore = null,
    httpRequest = null,
  }: {
    reduxStore?: Object,
    httpRequest?: Object,
  }): void => {
    // Always update httpRequest as it can be different for every init call
    RiseartLogger.httpRequest = httpRequest || RiseartLogger.httpRequest;

    if (RiseartLogger.isInitialized) {
      return;
    }

    if (RiseartLogger.isSentryEnabled) {
      // no need to set reduxStore if sentry is not enabled,
      // because we use it only when logging to sentry
      RiseartLogger.reduxStore = reduxStore || RiseartLogger.reduxStore;
    }

    RiseartLogger.initialized = true;
  },

  /**
   * collectStateData
   *
   * @param {Object} data
   * @returns {Object}
   */
  collectStateData: (data: Object = null): Object => {
    return {
      ...(data || {}),
      ...getReduxStateData(RiseartLogger.reduxStore),
      ...getHttpStateData(RiseartLogger.httpRequest),
    };
  },

  /**
   * exception
   *
   * @param {Error} exception
   * @param {Object} data
   * @returns {void}
   */
  exception: (exception: Object, data: Object = {}): void => {
    if (RiseartLogger.isSentryEnabled) {
      RiseartLogger.sentrySdk.withScope((scope) => {
        const logData = RiseartLogger.collectStateData(data);

        Object.keys(logData).forEach((k) => {
          scope.setExtra(k, logData[k]);
        });

        // Sometimes exception may be a plain object and it needs to be converted
        // to proper Error instance as Sentry accepts only Error instances in captureException
        let loggedException = exception;
        if (exception && !(exception instanceof Error)) {
          loggedException = new RiseArtSentryError(exception);
        }

        RiseartLogger.sentrySdk.captureException(loggedException);
      });
    }

    /* eslint no-console:0 */
    SHOW_CONSOLE_LOGS && typeof console === 'object' && console.error && console.error(exception);
  },

  /**
   * message
   *
   * @param {LogMessageType}
   * @returns {void}
   */
  message: ({ message, level = ERROR_LEVELS.INFO, data = {} }: LogMessageType): void => {
    if (SHOW_CONSOLE_LOGS) {
      if (level >= ERROR_LEVELS.INFO) {
        console.info(message, data);
      } else if (level >= ERROR_LEVELS.WARNING) {
        console.warn(message, data);
      } else if (level >= ERROR_LEVELS.ERROR) {
        console.error(message, data);
      } else {
        console.info(message, data);
      }
    }

    if (RiseartLogger.isSentryEnabled && level < ERROR_LEVELS.WARNING) {
      RiseartLogger.sentrySdk.withScope((scope) => {
        scope.setLevel(level);

        const logData = RiseartLogger.collectStateData(data);

        Object.keys(logData).forEach((k) => {
          scope.setExtra(k, logData[k]);
        });

        RiseartLogger.sentrySdk.captureMessage(message);
      });
    }
  },

  /**
   * groupedLog
   *
   * @param {string} title
   * @param {any} data
   * @returns void
   */
  groupedLog: (title: string, data: any): void => {
    if (!SHOW_CONSOLE_LOGS) {
      return;
    }

    console.groupCollapsed(title);
    console.log(data); // eslint-disable-line
    console.groupEnd();
  },

  /**
   * setUserContext
   *
   * @param {Object} me
   * @returns void
   */
  setUserContext: (me: Object): void => {
    if (!RiseartLogger.isSentryEnabled) {
      return;
    }

    RiseartLogger.sentrySdk.getCurrentScope().setUser({
      email: me.visitor.email,
      id: me.visitor.id,
      extra: me,
    });
  },
};
